import { Fragment, type ReactNode, useState } from "react";
import { Popover } from "@base-ui/react/popover";
import { MoreVertical } from "lucide-react";

import { Separator } from "@/shared/components/ui/separator";
import { cn } from "@/shared/lib/utils";

/**
 * A single menu item rendered inside a {@link CardActionMenu}.
 *
 * Items mirror the visual language of our default buttons (icon on the
 * leading edge, label on the right) but render as menu rows.
 */
export interface CardActionMenuItem {
  /** Stable key for React reconciliation. */
  key: string;
  /** Visible label. */
  label: string;
  /** Optional leading icon (a Lucide icon element). */
  icon?: ReactNode;
  /** Click handler. The menu auto-closes after invocation. */
  onSelect: () => void;
  /** When true, the item is rendered with destructive styling. */
  destructive?: boolean;
  /** When true, the item cannot be selected. */
  disabled?: boolean;
  /**
   * Optional section heading. Consecutive items sharing a section are drawn
   * together under it, with a divider where the section changes. The text is
   * the caller's (from data), never a constant here.
   */
  section?: string;
  /**
   * Optional short explanation, from data. Drawn once under the section heading
   * when every item in the section carries the same note (a section that is
   * locked for one reason), otherwise under the item's own label.
   */
  note?: string;
}

/**
 * The note each section's items all share, keyed by section. A section whose
 * items carry different notes (or none) is absent, and its items keep their own.
 */
function sharedSectionNotes(items: CardActionMenuItem[]): Map<string, string> {
  const notes = new Map<string, string>();
  const sections = new Set(items.map((item) => item.section).filter(Boolean));
  for (const section of sections) {
    const run = items.filter((item) => item.section === section);
    const note = run[0].note;
    if (note && run.every((item) => item.note === note)) {
      notes.set(section as string, note);
    }
  }
  return notes;
}

interface CardActionMenuProps {
  items: CardActionMenuItem[];
  /** Optional accessible label for the trigger. */
  triggerLabel?: string;
  /** Optional className appended to the trigger button. */
  triggerClassName?: string;
  /** Optional className appended to the popup. */
  contentClassName?: string;
  /** Test id forwarded to the trigger. */
  "data-testid"?: string;
}

/**
 * Card-scoped overflow menu rendered as a square 3-dot button matching the
 * height of our primary CTAs (e.g. `+ Claim`, `Transfer ownership`). The
 * trigger uses a white background with a hairline rule so it sits beside the
 * green CTA without competing visually. The dropdown itself echoes the same
 * button language: icon-on-left, label, hairline border, soft shadow.
 */
export function CardActionMenu({
  items,
  triggerLabel = "More actions",
  triggerClassName,
  contentClassName,
  "data-testid": dataTestId,
}: CardActionMenuProps) {
  const [open, setOpen] = useState(false);

  if (items.length === 0) return null;
  const sectionNotes = sharedSectionNotes(items);

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger
        type="button"
        aria-label={triggerLabel}
        title={triggerLabel}
        data-testid={dataTestId}
        className={cn(
          "inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-[6px] border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg-card)] text-[var(--ink-1)] transition-colors hover:bg-[var(--bg-subtle)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--green-bg)]",
          triggerClassName,
        )}
      >
        <MoreVertical className="h-4 w-4" />
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Backdrop className="fixed inset-0 z-40" />
        <Popover.Positioner side="bottom" align="end" sideOffset={6} className="isolate z-50">
          <Popover.Popup
            className={cn(
              "min-w-[200px] origin-(--transform-origin) overflow-hidden rounded-[8px] border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg-card)] p-1 shadow-[var(--shadow-float)] duration-100 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95",
              contentClassName,
            )}
          >
            {items.map((item, index) => {
              const previous = index > 0 ? items[index - 1].section : undefined;
              const sectionStarts = index === 0 || item.section !== previous;
              const sectionNote =
                sectionStarts && item.section
                  ? sectionNotes.get(item.section)
                  : undefined;
              const ownNote =
                item.section && sectionNotes.has(item.section)
                  ? undefined
                  : item.note;
              return (
              <Fragment key={item.key}>
                {sectionStarts && index > 0 && (
                  <Separator className="my-1 h-px bg-[var(--rule)]" />
                )}
                {sectionStarts && item.section && (
                  <div className="px-2.5 pb-1 pt-1.5 text-[10.5px] font-medium uppercase tracking-[0.12em] text-[var(--ink-2)]">
                    {item.section}
                  </div>
                )}
                {sectionNote && (
                  <p className="max-w-[280px] whitespace-normal px-2.5 pb-1.5 text-[11.5px] leading-snug text-[var(--ink-2)]">
                    {sectionNote}
                  </p>
                )}
              <button
                type="button"
                // The popup is portalled, so a label-based locator can match a
                // like-named button elsewhere on the record - every card has an
                // "Edit". Keyed here so a test can target this menu's item.
                data-testid={`card-action-${item.key}`}
                disabled={item.disabled}
                onClick={() => {
                  if (item.disabled) return;
                  item.onSelect();
                  setOpen(false);
                }}
                className={cn(
                  "flex w-full cursor-pointer items-center gap-2.5 rounded-[6px] px-2.5 py-2 text-left text-[12.5px] font-normal text-[var(--ink-1)] outline-none transition-colors hover:bg-[var(--bg-subtle)] focus-visible:bg-[var(--bg-subtle)] disabled:cursor-not-allowed disabled:opacity-50",
                  item.destructive &&
                    "text-[var(--status-error)] hover:bg-[color-mix(in_srgb,var(--status-error)_8%,var(--bg-card))]",
                )}
              >
                {item.icon && (
                  <span
                    className={cn(
                      "inline-flex h-4 w-4 shrink-0 items-center justify-center text-[var(--ink-2)] [&_svg]:h-4 [&_svg]:w-4",
                      item.destructive && "text-[var(--status-error)]",
                    )}
                  >
                    {item.icon}
                  </span>
                )}
                <span className="flex min-w-0 flex-col">
                  <span className="truncate">{item.label}</span>
                  {ownNote && (
                    <span className="max-w-[240px] whitespace-normal text-[11px] leading-snug text-[var(--ink-2)]">
                      {ownNote}
                    </span>
                  )}
                </span>
              </button>
              </Fragment>
              );
            })}
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  );
}
